import { getSession, signIn } from 'next-auth/react'
import { postSseStream, type StreamHandlers } from './sse'

export const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
export const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''

/**
 * A robust API client that handles authentication and base path resolution.
 * @param endpoint The API endpoint to call, e.g., '/api/test' or '/api/admin/dashboard'.
 *                 The endpoint should include the '/api' prefix.
 * @param options Standard fetch options (method, body, etc.).
 */
async function apiClientFetch<T = unknown>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const session = await getSession()

  const headers = new Headers(options.headers || {})

  if (session?.accessToken) {
    headers.set('Authorization', `Bearer ${session.accessToken}`)
  }

  // Handle session errors that occurred during a token refresh attempt.
  if (session?.error === 'RefreshAccessTokenError') {
    // Force sign-in to get a new, valid session.
    await signIn('keycloak')
    // Throw an error to stop the current, failing API call.
    throw new Error('Session expired. Please sign in again.')
  }

  // Construct the full URL: http://localhost:8001/app1/api/test
  const fullUrl = `${API_URL}${BASE_PATH}${endpoint}`

  const response = await fetch(fullUrl, { ...options, headers })

  // If the backend returns a 401, it means the token is invalid or expired.
  // This can happen if the backend is restarted or the user's session is revoked.
  // We trigger a re-login to fix the session.
  if (response.status === 401) {
    await signIn('keycloak')
    throw new Error('Your session has expired. Please sign in again.')
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      detail: response.statusText,
    }))
    throw new Error(errorData.detail || 'An API error occurred.')
  }

  // Handle responses with no content
  if (response.status === 204) {
    return null as T
  }

  return response.json() as Promise<T>
}

/**
 * API client with convenience methods for common HTTP operations.
 */
export const apiClient = {
  /**
   * Perform a GET request.
   */
  get: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'GET' })
  },

  /**
   * Perform a POST request.
   */
  post: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PUT request.
   */
  put: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PATCH request.
   */
  patch: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a DELETE request.
   */
  delete: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'DELETE' })
  },
}

/**
 * POST and read a server-sent events answer with the signed-in user's Bearer
 * token (plan 03 D18: `/api/ai/chat/stream`). The wire format, heartbeats and
 * abort handling live in lib/sse.ts; this adds only auth and the base URL.
 *
 * Unlike apiClient, a 401 here does not start a sign-in redirect: it rejects
 * with a StreamError before any text, and the caller falls back to the
 * whole-answer endpoint, which owns the session handling.
 */
export async function apiStreamPost(
  endpoint: string,
  body: unknown,
  handlers: StreamHandlers,
  options: { signal?: AbortSignal } = {}
): Promise<string> {
  const session = await getSession()
  const headers = new Headers()
  if (session?.accessToken) {
    headers.set('Authorization', `Bearer ${session.accessToken}`)
  }
  return postSseStream(`${API_URL}${BASE_PATH}${endpoint}`, body, handlers, { headers, signal: options.signal })
}

/**
 * Fetch a binary file (e.g. a generated PDF) with the same Bearer-token
 * auth as apiClient, and trigger a browser save-as-download for it.
 *
 * A plain `<a href>` can't be used for this: this app authenticates via
 * an Authorization header attached per-request (see apiClientFetch above),
 * not cookies, and a normal link navigation carries no custom headers.
 */
export async function downloadAuthenticatedFile(endpoint: string, filename: string): Promise<void> {
  const blob = await fetchAuthenticatedBlob(endpoint)
  saveBlob(blob, filename)
}

/**
 * Fetch a binary file with the recruiter's Bearer token and return it as a
 * Blob, e.g. to show a credential PDF in an iframe through a blob: URL
 * (04 §3.4). The caller owns the object URL it creates from the Blob.
 */
export async function fetchAuthenticatedBlob(endpoint: string, signal?: AbortSignal): Promise<Blob> {
  const session = await getSession()
  const headers = new Headers()
  if (session?.accessToken) {
    headers.set('Authorization', `Bearer ${session.accessToken}`)
  }

  const fullUrl = `${API_URL}${BASE_PATH}${endpoint}`
  const response = await fetch(fullUrl, { headers, signal })

  if (response.status === 401) {
    await signIn('keycloak')
    throw new Error('Your session has expired. Please sign in again.')
  }
  if (!response.ok) {
    throw new Error(`Download failed (${response.status})`)
  }

  return response.blob()
}

/** Trigger a browser save-as for a Blob already in memory. */
export function saveBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(objectUrl)
}

// Default export for backward compatibility
export default apiClientFetch
